import {
  FlexBox,
  SelectionListChangeEvent,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';
import { TagChips } from 'lib-shared-tagging';
import { ampli } from 'lib-shared-tracking';
import type { SuggestedTag, Tag } from 'lib-shared-types-api';
import { useCallback } from 'react';

import { conversationsApi } from 'apis/conversations';

import { SuggestedTagChips } from './components/suggestedTagChips';
import { TagsDropdownButton } from './components/tagsDropdownButton/tagsDropdownButton';
import styles from './conversationTags.module.scss';
import { useSuggestedTags } from './hooks/useSuggestedTags';

type ConversationTagsProps = {
  /** The id of the conversation. */
  csid: number;
};

/** Represents a component that presents the conversation tags. */
export const ConversationTags = ({
  csid,
}: ConversationTagsProps): JSX.Element => {
  const hasTagAccess = useHasAccess('conversation.tag');
  const { data, isFetching } = conversationsApi.useReadConversationQuery(
    {
      csid,
    },
    { refetchOnMountOrArgChange: true },
  );
  const [updateConversationTag] =
    conversationsApi.useUpdateConversationTagMutation();

  const mainConversation = data?.data.history.main_conversation;

  const handleChange = useCallback(
    ({
      selectedItem: { isSelected, value: tag },
    }: SelectionListChangeEvent<Tag>) => {
      if (isSelected) {
        updateConversationTag({ action: 'add', csid, tag });
        ampli.conversationHeaderTagAdded({ csid, tag: tag.name });
      } else {
        updateConversationTag({ action: 'remove', csid, tag });
        ampli.conversationHeaderTagRemoved({ csid, tag: tag.name });
      }
    },
    [csid, updateConversationTag],
  );

  const handleRemove = useCallback(
    (tag: Tag) => {
      updateConversationTag({ action: 'remove', csid, tag });
      ampli.conversationHeaderTagRemoved({ csid, tag: tag.name });
    },
    [csid, updateConversationTag],
  );

  const tags = mainConversation?.tags ?? [];
  const messageCount =
    (mainConversation?.attributes.inbound_message_count ?? 0) +
    (mainConversation?.attributes.outbound_message_count ?? 0);
  const { suggestions, isEnabled } = useSuggestedTags({
    csid,
    existingTags: tags,
    hasMessages: messageCount > 0,
  });

  const handleAddSuggestion = useCallback(
    (tag: SuggestedTag) => {
      updateConversationTag({
        action: 'add',
        csid,
        tag: {
          id: tag.id,
          name: tag.name,
          color: tag.color,
          type: 'conversation',
          usage_count: 0,
          is_deactivated: false,
        },
      });
      ampli.agentCopilotConversationTagSuggestionAccepted({
        csid,
        tag: tag.name,
      });
      ampli.conversationHeaderTagAdded({ csid, tag: tag.name });
    },
    [csid, updateConversationTag],
  );

  return (
    <FlexBox alignItems="center" gap="100" className={styles.tags}>
      <Tooltip
        content="You don't have permissions to add tags"
        disabled={hasTagAccess}
      >
        <TagsDropdownButton
          tags={tags}
          disabled={!hasTagAccess}
          isLoading={isFetching}
          onChange={handleChange}
        />
      </Tooltip>

      <TagChips
        tags={tags}
        limit={3}
        onRemove={hasTagAccess ? handleRemove : undefined}
        tagClassName={styles.tag}
        data-testid="conversation-tags"
      />

      {hasTagAccess && isEnabled && suggestions.length > 0 && (
        <SuggestedTagChips
          suggestions={suggestions}
          onAdd={handleAddSuggestion}
        />
      )}
    </FlexBox>
  );
};
